import { useState, useMemo } from "react";
import { useData } from "../../context/DataContext";
import { Card, Btn, Label, TextInput, NumberInput, DateInput, NativeSelect } from "../kit";
import { EXPENSE_CATEGORIES, uid, todayStr, fmtINR, createSpreadExpenseEntries, clientTotals } from "../../lib/finance";
import { toast } from "sonner";
import { Handshake, Banknote, UserPlus, User, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";

const KINDS = [
  { id: "collab", label: "Brand Collab", icon: Handshake },
  { id: "expense", label: "Expense", icon: Banknote },
  { id: "newclient", label: "New Client", icon: UserPlus },
  { id: "existingclient", label: "Existing Client", icon: User },
];

export default function AddEntry({ prefillClient }) {
  const [kind, setKind] = useState(prefillClient ? "existingclient" : "collab");
  return (
    <div className="max-w-xl mx-auto">
      <Card className="p-5">
        <h2 className="text-lg font-semibold text-slate-900 font-head">What's this entry for?</h2>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {KINDS.map((k) => {
            const Icon = k.icon;
            return (
              <button key={k.id} data-testid={`kind-${k.id}`} onClick={() => setKind(k.id)}
                className={cn("flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors",
                  kind === k.id ? "border-primary bg-accent text-accent-foreground" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
                <Icon className="h-4 w-4" /> {k.label}
              </button>
            );
          })}
        </div>
        <div className="mt-5">
          {kind === "collab" && <CollabForm />}
          {kind === "expense" && <ExpenseForm />}
          {kind === "newclient" && <NewClientForm />}
          {kind === "existingclient" && <ExistingClientForm prefillClient={prefillClient} />}
        </div>
      </Card>
    </div>
  );
}

function CollabForm() {
  const { entries, saveEntries, pipeline, savePipeline } = useData();
  const [mode, setMode] = useState("final");
  const [f, setF] = useState({ desc: "", amount: "", date: todayStr(), brand: "", status: "Pipeline", notes: "", advance: "", advanceDate: todayStr() });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const saveFinal = async () => {
    const amount = parseFloat(f.amount);
    if (!amount || amount <= 0 || !f.date) return toast.error("Enter a valid amount and date");
    await saveEntries([...entries, { id: uid(), type: "income", category: "Brand Collabs", amount, description: f.desc.trim() || "Brand collab", date: f.date }]);
    setF({ ...f, desc: "", amount: "" });
    toast.success("Collab income saved");
  };
  const savePipe = async () => {
    const brand = f.brand.trim();
    if (!brand) return toast.error("Enter a brand name");
    const advance = parseFloat(f.advance) || 0;
    if (advance < 0) return toast.error("Advance can't be negative");
    if (advance > 0 && !f.advanceDate) return toast.error("Enter the date the advance was received");
    const id = uid();
    let advances = [];
    if (advance > 0) {
      // Advance received up front: goes into the ledger now; the green tick later logs only the balance.
      const entryId = uid();
      await saveEntries([...entries, { id: entryId, type: "income", category: "Brand Collabs", amount: advance, description: `${brand} — advance (pipeline)`, date: f.advanceDate, pipelineId: id }]);
      advances = [{ id: uid(), amount: advance, date: f.advanceDate, entryId }];
    }
    await savePipeline([...pipeline, { id, brand, expectedAmount: parseFloat(f.amount) || 0, expectedDate: f.date, status: f.status, notes: f.notes.trim(), createdDate: todayStr(), advances }]);
    setF({ ...f, brand: "", amount: "", notes: "", advance: "", advanceDate: todayStr() });
    toast.success(advance > 0 ? `${brand} added to pipeline — ${fmtINR(advance)} advance added to the ledger` : `${brand} added to pipeline`);
  };

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Toggle active={mode === "final"} onClick={() => setMode("final")}>✅ Confirmed / Paid</Toggle>
        <Toggle active={mode === "pipeline"} onClick={() => setMode("pipeline")}>⏳ Upcoming / Pipeline</Toggle>
      </div>
      {mode === "final" ? (
        <>
          <Label>Brand / description</Label>
          <TextInput value={f.desc} onChange={set("desc")} placeholder="e.g. Digitalist Techmedia — Instagram collab" data-testid="collab-desc" />
          <Label>Amount (₹)</Label>
          <NumberInput value={f.amount} onChange={set("amount")} placeholder="0" data-testid="collab-amount" />
          <Label>Date</Label>
          <DateInput value={f.date} onChange={set("date")} data-testid="collab-date" />
          <Btn className="mt-4 w-full" onClick={saveFinal} data-testid="save-collab">Save collab income</Btn>
        </>
      ) : (
        <>
          <Label>Brand name</Label>
          <TextInput value={f.brand} onChange={set("brand")} placeholder="e.g. Nutriva Foods" data-testid="collab-brand" />
          <Label>Expected amount (₹)</Label>
          <NumberInput value={f.amount} onChange={set("amount")} placeholder="0" />
          <Label>Expected date (approx.)</Label>
          <DateInput value={f.date} onChange={set("date")} />
          <Label>Advance received (₹, optional)</Label>
          <NumberInput value={f.advance} onChange={set("advance")} placeholder="0 — leave blank if nothing received yet" data-testid="collab-advance" />
          {parseFloat(f.advance) > 0 && (
            <>
              <Label>Advance received on</Label>
              <DateInput value={f.advanceDate} onChange={set("advanceDate")} data-testid="collab-advance-date" />
            </>
          )}
          <Label>Stage</Label>
          <NativeSelect value={f.status} onChange={set("status")}>
            <option>Pipeline</option><option>In Talks</option><option>Verbally Confirmed</option>
          </NativeSelect>
          <Label>Notes (optional)</Label>
          <TextInput value={f.notes} onChange={set("notes")} placeholder="e.g. waiting on contract" />
          <Btn className="mt-4 w-full" onClick={savePipe} data-testid="save-collab-pipeline">Add to pipeline</Btn>
        </>
      )}
    </div>
  );
}

function ExpenseForm() {
  const { entries, saveEntries } = useData();
  const [f, setF] = useState({ category: EXPENSE_CATEGORIES[0].name, desc: "", amount: "", date: todayStr(), spread: false, months: 3 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    const amount = parseFloat(f.amount);
    if (!amount || amount <= 0 || !f.date) return toast.error("Enter a valid amount and date");
    const desc = f.desc.trim() || f.category;
    let next;
    if (f.spread && f.months > 1) {
      next = [...entries, ...createSpreadExpenseEntries(f.category, desc, amount, parseInt(f.months), f.date, uid())];
      toast.success(`Spread ₹${Math.round(amount).toLocaleString("en-IN")} across ${f.months} months`);
    } else {
      next = [...entries, { id: uid(), type: "expense", category: f.category, amount, description: desc, date: f.date }];
      toast.success("Expense saved");
    }
    await saveEntries(next);
    setF({ ...f, desc: "", amount: "", spread: false });
  };

  return (
    <div>
      <Label>Category</Label>
      <NativeSelect value={f.category} onChange={set("category")} data-testid="expense-category">
        {EXPENSE_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      </NativeSelect>
      <Label>Description</Label>
      <TextInput value={f.desc} onChange={set("desc")} placeholder="e.g. Meta Ads — 17 May" data-testid="expense-desc" />
      <Label>Amount (₹)</Label>
      <NumberInput value={f.amount} onChange={set("amount")} placeholder="0" data-testid="expense-amount" />
      <Label>Date</Label>
      <DateInput value={f.date} onChange={set("date")} data-testid="expense-date" />
      <label className="flex items-center gap-2 mt-4 text-sm text-slate-600">
        <input type="checkbox" checked={f.spread} onChange={(e) => setF({ ...f, spread: e.target.checked })} data-testid="expense-spread-toggle" className="h-4 w-4 rounded border-slate-300" />
        Lump-sum / quarterly — spread it evenly
      </label>
      {f.spread && (
        <>
          <Label>Spread across how many months</Label>
          <NumberInput value={f.months} min={2} max={12} onChange={set("months")} />
          <p className="text-xs text-muted-foreground mt-1">e.g. ₹90,000 over 3 months logs ₹30,000/month automatically.</p>
        </>
      )}
      <Btn className="mt-4 w-full" onClick={save} data-testid="save-expense">Save expense</Btn>
    </div>
  );
}

function NewClientForm() {
  const { entries, saveEntries, clients, saveClients } = useData();
  const [f, setF] = useState({ name: "", date: todayStr(), plan: "", amount: "", duration: "", balance: "" });
  const planN = parseFloat(f.plan) || 0, paidN = parseFloat(f.amount) || 0;
  const autoBalance = f.balance === "" ? Math.max(planN - paidN, 0) : parseFloat(f.balance) || 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter the client's name");
    if (!f.date) return toast.error("Pick a date");
    if (!planN && !paidN) return toast.error("Enter a plan value or amount paid");
    const duration = parseInt(f.duration) || null;
    const clientId = uid();
    const planTxn = { id: uid(), date: f.date, type: "plan", amount: paidN, planAmount: planN, duration, note: `New plan — ${duration || "?"} month(s)` };
    let nextEntries = entries;
    if (paidN > 0) {
      const entryId = uid();
      planTxn.entryId = entryId;
      nextEntries = [...entries, { id: entryId, type: "income", category: "Nutrition Consultation", amount: paidN, description: `${f.name.trim()} — new plan payment`, date: f.date, clientId }];
    }
    const client = { id: clientId, name: f.name.trim(), createdDate: f.date, balance: autoBalance, transactions: [planTxn] };
    await saveClients([...clients, client]);
    if (paidN > 0) await saveEntries(nextEntries);
    setF({ name: "", date: todayStr(), plan: "", amount: "", duration: "", balance: "" });
    toast.success(`${client.name} added — balance ${fmtINR(autoBalance)}`);
  };

  return (
    <div>
      <Label>Client full name</Label>
      <TextInput value={f.name} onChange={set("name")} placeholder="e.g. Rhea Kapoor" data-testid="nc-name" />
      <Label>Date</Label>
      <DateInput value={f.date} onChange={set("date")} />
      <Label>Total plan value (₹)</Label>
      <NumberInput value={f.plan} onChange={set("plan")} placeholder="e.g. 60000" data-testid="nc-plan" />
      <Label>Amount paid now (₹)</Label>
      <NumberInput value={f.amount} onChange={set("amount")} placeholder="0" data-testid="nc-amount" />
      <Label>Duration of plan (months)</Label>
      <NumberInput value={f.duration} onChange={set("duration")} placeholder="e.g. 3" />
      <Label>Balance remaining (₹) — auto, editable</Label>
      <NumberInput value={f.balance === "" ? autoBalance : f.balance} onChange={set("balance")} />
      <Btn className="mt-4 w-full" onClick={save} data-testid="save-new-client">Save new client</Btn>
    </div>
  );
}

function ExistingClientForm({ prefillClient }) {
  const { entries, saveEntries, clients, saveClients } = useData();
  const [sel, setSel] = useState(prefillClient || "");
  const [purpose, setPurpose] = useState("clearing");
  const [c, setC] = useState({ cDate: todayStr(), cAmount: "", pDate: todayStr(), pPlan: "", pDuration: "", pAmount: "" });
  const [edit, setEdit] = useState(false);
  const [editVals, setEditVals] = useState({ name: "", balance: "" });
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const sorted = useMemo(() => clients.slice().sort((a, b) => a.name.localeCompare(b.name)), [clients]);
  const client = clients.find((x) => x.id === sel);
  const totals = client ? clientTotals(client) : null;
  const txns = useMemo(
    () => (client ? client.transactions.slice().sort((a, b) => b.date.localeCompare(a.date)) : []),
    [client]
  );

  const updateClient = async (updater, extraEntries) => {
    const next = clients.map((x) => (x.id === client.id ? updater(x) : x));
    await saveClients(next);
    if (extraEntries) await saveEntries(extraEntries);
  };

  const saveClearing = async () => {
    const amount = parseFloat(c.cAmount);
    if (!c.cDate || !amount || amount <= 0) return toast.error("Enter a valid amount and date");
    const entryId = uid();
    const newBalance = Math.max((client.balance || 0) - amount, 0);
    await updateClient(
      (x) => ({ ...x, balance: newBalance, transactions: [...x.transactions, { id: uid(), date: c.cDate, type: "payment", amount, note: "Balance payment", entryId }] }),
      [...entries, { id: entryId, type: "income", category: "Nutrition Consultation", amount, description: `${client.name} — balance payment`, date: c.cDate, clientId: client.id }]
    );
    setC({ ...c, cAmount: "" });
    toast.success(`Payment recorded — balance now ${fmtINR(newBalance)}`);
  };

  const saveNewPlan = async () => {
    const planAmount = parseFloat(c.pPlan) || 0, amount = parseFloat(c.pAmount) || 0;
    if (!c.pDate || (!planAmount && !amount)) return toast.error("Enter a plan value or amount paid");
    const duration = parseInt(c.pDuration) || null;
    const newBalance = Math.max((client.balance || 0) + planAmount - amount, 0);
    const txn = { id: uid(), date: c.pDate, type: "plan", amount, planAmount, duration, note: `New plan — ${duration || "?"} month(s)` };
    let extra = null;
    if (amount > 0) {
      const entryId = uid(); txn.entryId = entryId;
      extra = [...entries, { id: entryId, type: "income", category: "Nutrition Consultation", amount, description: `${client.name} — new plan payment`, date: c.pDate, clientId: client.id }];
    }
    await updateClient((x) => ({ ...x, balance: newBalance, transactions: [...x.transactions, txn] }), extra);
    setC({ ...c, pPlan: "", pDuration: "", pAmount: "" });
    toast.success(`New plan added — balance now ${fmtINR(newBalance)}`);
  };

  const delTxn = async (txn) => {
    if (!window.confirm("Delete this transaction? Updates balance and removes it from the ledger.")) return;
    let bal = client.balance || 0;
    if (!txn.historical) {
      if (txn.type === "payment") bal = bal + (txn.amount || 0);
      else if (txn.type === "plan") bal = bal - (txn.planAmount || 0) + (txn.amount || 0);
    }
    const nextEntries = txn.entryId ? entries.filter((e) => e.id !== txn.entryId) : entries;
    await updateClient((x) => ({ ...x, balance: Math.max(bal, 0), transactions: x.transactions.filter((t) => t.id !== txn.id) }), nextEntries);
    toast.success("Transaction deleted");
  };

  const openEdit = () => { setEditVals({ name: client.name, balance: client.balance || 0 }); setEdit(true); };
  const saveEdit = async () => {
    if (!editVals.name.trim()) return toast.error("Name can't be empty");
    await updateClient((x) => ({ ...x, name: editVals.name.trim(), balance: Math.max(parseFloat(editVals.balance) || 0, 0) }));
    setEdit(false);
    toast.success("Client details updated");
  };

  return (
    <div>
      <Label>Select client</Label>
      <NativeSelect value={sel} onChange={(e) => { setSel(e.target.value); setEdit(false); }} data-testid="ec-select">
        <option value="">— choose a client —</option>
        {sorted.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
      </NativeSelect>

      {client && (
        <div className="mt-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Total paid</p>
              <p className="text-lg font-bold tnum text-slate-900">{fmtINR(totals.paid)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Balance</p>
              {client.balance > 0
                ? <span className="inline-block rounded-full bg-rose-50 text-rose-700 px-3 py-1 text-sm font-semibold">{fmtINR(client.balance)} owed</span>
                : <span className="inline-block rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-sm font-semibold">Cleared</span>}
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <h3 className="font-semibold text-slate-800">Payment history</h3>
            <button onClick={openEdit} className="text-xs font-semibold text-primary hover:underline" data-testid="edit-client-toggle">✎ Edit client</button>
          </div>

          {edit && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mt-2">
              <Label>Client name</Label>
              <TextInput value={editVals.name} onChange={(e) => setEditVals({ ...editVals, name: e.target.value })} />
              <Label>Balance (₹) — manual override</Label>
              <NumberInput value={editVals.balance} onChange={(e) => setEditVals({ ...editVals, balance: e.target.value })} />
              <Btn className="mt-3" onClick={saveEdit} data-testid="save-client-edit">Save client details</Btn>
            </div>
          )}

          <div className="mt-2 divide-y divide-slate-100">
            {txns.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <span className="text-slate-500 mr-2">{t.date}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", t.type === "plan" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")}>{t.type === "plan" ? "New plan" : "Payment"}</span>
                  {t.note && <span className="text-slate-400 ml-2">{t.note}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold tnum text-slate-900">{fmtINR(t.amount)}</span>
                  <button onClick={() => delTxn(t)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-200">
            <div className="flex gap-2 mb-2">
              <Toggle active={purpose === "clearing"} onClick={() => setPurpose("clearing")}>Clearing balance</Toggle>
              <Toggle active={purpose === "newplan"} onClick={() => setPurpose("newplan")}>New nutrition plan</Toggle>
            </div>
            {purpose === "clearing" ? (
              <>
                <Label>Date</Label>
                <DateInput value={c.cDate} onChange={set("cDate")} />
                <Label>Amount received (₹)</Label>
                <NumberInput value={c.cAmount} onChange={set("cAmount")} placeholder="0" data-testid="ec-clearing-amount" />
                <Btn className="mt-4 w-full" onClick={saveClearing} data-testid="save-existing-client">Save payment</Btn>
              </>
            ) : (
              <>
                <Label>Date</Label>
                <DateInput value={c.pDate} onChange={set("pDate")} />
                <Label>New plan value (₹)</Label>
                <NumberInput value={c.pPlan} onChange={set("pPlan")} placeholder="e.g. 60000" />
                <Label>Duration (months)</Label>
                <NumberInput value={c.pDuration} onChange={set("pDuration")} placeholder="e.g. 3" />
                <Label>Amount paid now (₹)</Label>
                <NumberInput value={c.pAmount} onChange={set("pAmount")} placeholder="0" />
                <Btn className="mt-4 w-full" onClick={saveNewPlan} data-testid="save-existing-client">Save new plan</Btn>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ active, children, ...props }) {
  return (
    <button {...props} className={cn("flex-1 rounded-xl px-3 py-2 text-sm font-semibold border transition-colors",
      active ? "border-primary bg-accent text-accent-foreground" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
      {children}
    </button>
  );
}
